import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import { useAuth } from '@/layouts/RootLayout';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DetailSkeleton } from '@/components/PageSkeleton';
import { Busy, ErrorState, Field, PageHeader, Panel } from '@/components/kit';
import { PickSelect, SwitchRow } from '@/components/inputs';
import { findMyCustomer } from '@/shared/account';
import { CONTACT_METHOD_VALUES } from '@/shared/constants';
import { contactPlaceholder, newCode } from '@/shared/pricing';
import { committed, displayNameOf, nowIso } from '@/shared/data';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';
import { setUser } from '@/store/userSlice';
import AccountDialog from '@/components/AccountDialog';
import { saveAccount, splitName } from '@/shared/userAccounts';
import { useT } from '@/shared/i18n';
import { A } from '@/shared/locales/account/keys';
import { B } from '@/shared/locales/account2/keys';

export const route = { path: '/account/preferences', layout: 'owner', access: "profile === 'Customer_c'" };
export const nav = { icon: 'SlidersHorizontal', label: 'Preferences', section: 'My account', profiles: ['Customer_c'], order: 60 };

function toForm(me, user) {
  return {
    Name: me?.Name ?? displayNameOf(user),
    contact_method_c: me?.contact_method_c || 'Discord',
    contact_handle_c: me?.contact_handle_c ?? '',
    marketing_opt_in_c: !!me?.marketing_opt_in_c,
    expiry_reminders_c: me ? !!me.expiry_reminders_c : true,
  };
}

export default function Preferences() {
  const { t } = useT();
  const { user } = useAuth();
  const { data: me, loading, error, run } = useFetch(() => findMyCustomer(user), [user?.emailAddress]);
  const [form, setForm] = useState(() => toForm(null, user));
  const [saving, setSaving] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const dispatch = useDispatch();
  const detailsTarget = useMemo(
    () =>
      detailsOpen
        ? { id: user?.userId, email: user?.emailAddress, firstName: user?.firstName, lastName: user?.lastName, name: me?.Name }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [detailsOpen],
  );

  useEffect(() => {
    if (me !== undefined) setForm(toForm(me, user));
  }, [me]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  async function save(e) {
    e.preventDefault();
    if (!form.Name.trim()) {
      toast.error(t(B.addName));
      return;
    }
    const fields = {
      Name: form.Name.trim(),
      contact_method_c: form.contact_method_c,
      contact_handle_c: form.contact_handle_c.trim(),
      marketing_opt_in_c: !!form.marketing_opt_in_c,
      expiry_reminders_c: !!form.expiry_reminders_c,
    };
    setSaving(true);
    try {
      let res;
      if (me) {
        const unsubscribed = form.marketing_opt_in_c
          ? null
          : me.marketing_opt_in_c
            ? nowIso()
            : (me.unsubscribed_at_c ?? null);
        res = await sdk.table('customers_c').update({ Id: me.Id, ...fields, unsubscribed_at_c: unsubscribed });
      } else {
        res = await sdk.table('customers_c').create({
          ...fields,
          email_c: user?.emailAddress ?? '',
          referral_code_c: newCode('REF-', 6),
        });
      }
      if (!committed(res, t(B.prefsSaved))) return;
      if (user?.userId != null && fields.Name !== displayNameOf(user)) {
        const { firstName, lastName } = splitName(fields.Name);
        const acc = await saveAccount(
          { Id: user.userId, Email: user.emailAddress },
          { firstName, lastName, email: user.emailAddress },
        );
        if (acc.ok) dispatch(setUser({ ...user, firstName, lastName, name: fields.Name }));
      }
      run();
    } finally {
      setSaving(false);
    }
  }

  async function copyReferral() {
    try {
      await navigator.clipboard.writeText(me.referral_code_c);
      toast.success(t(B.refCopied));
    } catch {
      toast.error(t(B.cantCopyIt));
    }
  }

  if (loading && me === undefined) {
    return (
      <div className="mx-auto max-w-4xl">
        <DetailSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl">
        <ErrorState message={error} onRetry={run} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        eyebrow={t('My account')}
        title={t(A.navPrefs)}
        description={t(B.prefsDesc)}
      />

      <form onSubmit={save} className="space-y-6">
        <Panel title={t(B.profile)} icon="UserRound" bodyClassName="space-y-4 py-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t(B.name)} htmlFor="p-name">
              <Input
                id="p-name"
                value={form.Name}
                onChange={(e) => set('Name')(e.target.value)}
                placeholder="Maya Okafor"
              />
            </Field>
            <Field label={t(B.email)} htmlFor="p-email" hint={t(B.emailHint)}>
              <div className="flex gap-2">
                <Input id="p-email" value={user?.emailAddress ?? ''} readOnly disabled placeholder="you@example.com" />
                <Button type="button" variant="outline" onClick={() => setDetailsOpen(true)}>
                  <ApperIcon name="PenLine" size={16} />
                  {t(B.change)}
                </Button>
              </div>
            </Field>
            <Field label={t(B.contactApp)} htmlFor="p-method">
              <PickSelect
                id="p-method"
                value={form.contact_method_c}
                onChange={set('contact_method_c')}
                values={CONTACT_METHOD_VALUES}
                placeholder={B.chooseApp}
              />
            </Field>
            <Field label={t(B.handle)} htmlFor="p-handle" hint={t(B.handleHint)}>
              <Input
                id="p-handle"
                value={form.contact_handle_c}
                onChange={(e) => set('contact_handle_c')(e.target.value)}
                placeholder={contactPlaceholder(form.contact_method_c)}
              />
            </Field>
          </div>
        </Panel>

        <Panel title={t(B.email)} icon="Mail" bodyClassName="space-y-3 py-4">
          <SwitchRow
            id="p-expiry"
            label={t(B.expiry)}
            hint={t(B.expiryHint)}
            checked={form.expiry_reminders_c}
            onChange={set('expiry_reminders_c')}
          />
          <SwitchRow
            id="p-marketing"
            label={t(B.offersNews)}
            hint={t(B.offersHint)}
            checked={form.marketing_opt_in_c}
            onChange={set('marketing_opt_in_c')}
          />
          {me?.unsubscribed_at_c && !me.marketing_opt_in_c && (
            <p className="text-xs text-muted-foreground">
              {t(B.unsubOn, { date: formatLocalDate(me.unsubscribed_at_c, DATE_FORMATS.SHORT) })}
            </p>
          )}
        </Panel>

        {me?.referral_code_c && (
          <Panel title={t(B.refCode)} icon="Gift" bodyClassName="py-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-mono text-lg tracking-wider text-primary">{me.referral_code_c}</p>
                <p className="text-xs text-muted-foreground">{t(B.refCodeHint)}</p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={copyReferral}>
                <ApperIcon name="Copy" size={16} />
                {t('Copy')}
              </Button>
            </div>
          </Panel>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button type="button" variant="ghost" asChild>
            <Link to="/change-password">
              <ApperIcon name="KeyRound" size={16} />
              {t('Change password')}
            </Link>
          </Button>
          <Button type="submit" disabled={saving}>
            <Busy busy={saving} label={me ? t(B.savePrefs) : t(B.createProfile)} busyLabel={t(B.saving)} />
          </Button>
        </div>
      </form>

      <AccountDialog
        target={detailsTarget}
        self
        onOpenChange={setDetailsOpen}
        onSaved={() => run()}
        description={t(B.detailsDesc)}
      />
    </div>
  );
}
